import { useCallback, useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';

function maintIdFromActionUrl(actionUrl) {
  const s = String(actionUrl || '').trim();
  if (!s.startsWith('maint:')) return null;
  const id = s.slice(5).trim();
  return id || null;
}

function resolveNotificationAction(notification) {
  const actionUrl = String(notification?.action_url || '').trim();
  const sourceType = String(notification?.source_type || '').trim();
  const sourceId = String(notification?.source_id || '').trim();
  const maintenanceId = maintIdFromActionUrl(actionUrl) || (sourceType === 'maintenance' ? sourceId : '');
  if (maintenanceId) return { kind: 'maintenance', id: maintenanceId };
  if (actionUrl) return { kind: 'url', url: actionUrl };
  return null;
}

export function NotificationBell({ onNotificationAction, unreadCount = 0, onUnreadCountChange }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getNotifications();
      setItems(Array.isArray(res.data?.notifications) ? res.data.notifications : []);
      onUnreadCountChange?.(Number(res.data?.unread_count ?? 0));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  useEffect(() => {
    if (!open) return;
    void loadList();
  }, [open, loadList]);

  const refreshBadgeOnly = useCallback(async () => {
    try {
      const res = await api.getNotificationsUnreadCount();
      onUnreadCountChange?.(Number(res.data?.unread_count ?? 0));
    } catch {
      // non-fatal
    }
  }, [onUnreadCountChange]);

  const markRead = async (id) => {
    try {
      await api.patchNotification(id, { status: 'read' });
      await loadList();
      await refreshBadgeOnly();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update notification');
    }
  };

  const dismiss = async (id) => {
    try {
      await api.patchNotification(id, { status: 'dismissed' });
      await loadList();
      await refreshBadgeOnly();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to dismiss notification');
    }
  };

  const openNotification = async (notif, { markOpenedAsRead }) => {
    const action = resolveNotificationAction(notif);
    if (!action) {
      toast.message('Nothing to open for this notification');
      return;
    }
    if (typeof onNotificationAction === 'function') {
      onNotificationAction(action, notif);
    }
    if (markOpenedAsRead && String(notif.status) === 'unread') {
      try {
        await api.patchNotification(notif.id, { status: 'read' });
      } catch {
        /* non-fatal */
      }
      await refreshBadgeOnly();
      if (open) await loadList();
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'relative h-7 gap-1 border-gray-200 px-2 text-[11px] text-gray-700',
            unreadCount > 0 && 'border-amber-200 bg-amber-50/70',
          )}
          data-testid="notification-bell-trigger"
          title="Notifications"
        >
          <Bell className="h-4 w-4 shrink-0" />
          <span className="sr-only">Notifications</span>
          {unreadCount > 0 ? (
            <span
              className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white"
              data-testid="notification-bell-badge"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[min(100vw-2rem,420px)] max-h-[min(70vh,520px)] overflow-y-auto border-gray-200 p-3 shadow-lg"
      >
        <div className="mb-2 flex items-start justify-between gap-2 border-b border-gray-100 pb-2">
          <div>
            <div className="text-sm font-semibold text-gray-900">Notifications</div>
            <div className="text-[11px] text-gray-500">Maintenance and system alerts</div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-[11px] text-gray-600"
            disabled={loading}
            onClick={() => void loadList()}
          >
            Refresh
          </Button>
        </div>
        {loading && items.length === 0 ? (
          <div className="py-6 text-center text-xs text-gray-500">Loading…</div>
        ) : null}
        {!loading && items.length === 0 ? (
          <div className="py-6 text-center text-xs text-gray-500">No active notifications</div>
        ) : null}
        <ul className="space-y-2">
          {items.map((n) => (
            <li
              key={n.id}
              className={cn(
                'rounded-lg border px-2.5 py-2 text-left',
                n.status === 'unread' ? 'border-amber-100 bg-amber-50/50' : 'border-gray-100 bg-white',
                n.priority === 'high' && n.status === 'unread' && 'border-orange-200',
              )}
            >
              <div className="text-[12px] font-semibold text-gray-900">{n.title}</div>
              <pre className="mt-1.5 whitespace-pre-wrap font-sans text-[11px] leading-snug text-gray-700">{n.message}</pre>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="h-7 text-[11px]"
                  onClick={() => openNotification(n, { markOpenedAsRead: true })}
                  disabled={!resolveNotificationAction(n)}
                >
                  {n.metadata?.cta || 'Open'}
                </Button>
                {n.status === 'unread' ? (
                  <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => markRead(n.id)}>
                    Mark read
                  </Button>
                ) : null}
                <Button type="button" variant="ghost" size="sm" className="h-7 text-[11px] text-gray-600" onClick={() => dismiss(n.id)}>
                  Dismiss
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
